import { type DependencyList, type EffectCallback, useEffect, useRef } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function usePrevious(value: any, hookFun: (effect: EffectCallback, deps?: DependencyList) => void = useEffect) {
	const ref = useRef();

	hookFun(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

/**
 * A very similar to useEffect hook, but it only calls the callback when all dependencies change.
 *
 * @param	fn - A callback function, that is gonna be called when all dependencies change.
 * @param deps - dependencies list, like in useEffect
 * @param hookFun - a hook function to use, by default it's useEffect
 */
export default function useEffectAllDepsChange(
	fn: EffectCallback,
	deps: DependencyList,
	hookFun: (effect: EffectCallback, deps?: DependencyList) => void = useEffect,
) {
	const prevDeps = usePrevious(deps);
	const changeTarget = useRef<DependencyList | undefined>();

	hookFun(() => {
		// nothing to compare to yet
		if (changeTarget.current === undefined) {
			changeTarget.current = prevDeps;
		}

		// we're mounting, so call the callback
		if (changeTarget.current === undefined) {
			return fn();
		}

		// make sure every dependency has changed
		if (changeTarget.current.every((dep, i) => dep !== deps[i])) {
			changeTarget.current = deps;

			return fn();
		}
	}, [fn, prevDeps, deps]);
}
